import MessageProcessor from "./message";
import sha256 from "crypto-js/sha256";
import { getSDK as getUnlinkSDK } from "../sdk/UnlinkInstance";
import { getAllLocalRatchetingKeys } from "../db/RatchetedConversationKeys";
import { loadLocalKeys } from "../db/KeyStorageManager";
import bs58 from 'bs58';
import { MATCHER_ENDPOINTS } from "../Config";

async function fetchChallenge() {
  const res = await fetch(MATCHER_ENDPOINTS.POW_ENDPOINT);
  if (!res.ok) throw new Error("Failed to get PoW challenge");
  return res.json();
}

function hashChallenge(challenge, nonce) {
  const input = JSON.stringify({ ...challenge, nonce });
  return sha256(input).toString();
}

function hasLeadingZeros(hash, difficulty) {
  const binaryHash = BigInt(`0x${hash}`).toString(2).padStart(256, "0");
  return binaryHash.startsWith("0".repeat(difficulty));
}

async function solveChallenge(challenge) {
  const { difficulty } = challenge;
  let nonce = 0;
  let hash;

  const CHECK_INTERVAL = 5000;

  while (true) {
    hash = hashChallenge(challenge, nonce);
    if (hasLeadingZeros(hash, difficulty)) break;
    nonce++;

    if (nonce % CHECK_INTERVAL === 0) {
      await new Promise(requestAnimationFrame);
    }
  }

  return { nonce, hash };
}

let socket = null;
let connecting = false;

const WebSocketService = {
  init: async ({ password, userId, salt }) => {  

    if (socket || connecting) return; // skip if already connected or connecting
    if (!userId || !password || !salt) return; // skip if no auth credentials available

    connecting = true;

    const unlinkSdk = getUnlinkSDK();
    const { viewPublicKey: localIdentityViewPublicKey } = await loadLocalKeys({userId, password, salt});

    const bitPrecision = 2;

    try {
      const response = await fetchChallenge();
      const challenge = response.challenge;
      const signature = response.signature;
      const solution = await solveChallenge(challenge);

      const payload = {
        challenge,
        nonce: solution.nonce,
        signature,
      };

      const encodedPayload = encodeURIComponent(JSON.stringify(payload));
      const wsUrl = `${MATCHER_ENDPOINTS.WS_ENDPOINT}?payload=${encodedPayload}`;
      socket = new WebSocket(wsUrl);
      socket.binaryType = "arraybuffer";

      await new Promise((resolve, reject) => {
        socket.onopen = () => resolve();
        socket.onerror = (err) => reject(err);
      });

      console.log("[WebSocket] Connected");

      const tags = unlinkSdk.deriveFilterTags({
        viewKey: localIdentityViewPublicKey,
        bits: bitPrecision,
        count: 1024,
      });

      const items = tags.map((tag) => Buffer.from(tag).toString("hex"));
      const msg = {
        type: "bloom_update",
        items,
        bit_precision: bitPrecision,
      };

      socket.send(JSON.stringify(msg));

      let rawBytesPrev;
      const allRatchetedViewKeys = await getAllLocalRatchetingKeys({ userId, password, salt });

      socket.onmessage = async (event) => {
        try {

          const rawBytes = new Uint8Array(event.data);

          if (rawBytes !== rawBytesPrev) {  
            rawBytesPrev = rawBytes;

            const allLocalRatchetedRecords = await getAllLocalRatchetingKeys({ userId, password, salt });

            for (const { localViewKeys, sessionKyberKeys } of allLocalRatchetedRecords) {
              for (let i = 0; i < localViewKeys.length; i++) {
                const { 
                  viewPublicKey: localViewPublicKey, 
                  viewPrivateKey: localViewPrivateKey, 
                } = localViewKeys[i];

                await MessageProcessor.process({
                  blobData: rawBytes,
                  receiverIdentityViewPublicKey: localIdentityViewPublicKey,
                  receiverViewPublicKey: localViewPublicKey,
                  receiverViewPrivateKey: localViewPrivateKey,
                  sessionKyberKeys,
                  password,
                  userId,
                  salt,
                });
              }
            }
          }
          else {
            console.warn("Duplicate rawbytes: ignored");
          }
        } catch (err) {
          console.error("[WebSocket] Failed to process:", err);
        }
      };

      socket.onclose = () => {
        console.warn("[WebSocket] Disconnected. Reconnecting in 5s...");
        socket = null;
        connecting = false;
        setTimeout(() => WebSocketService.init({ password, userId, salt }), 5000);
      };

      socket.onerror = (err) => {
        console.error("[WebSocket] Runtime error:", err);
      };

    } catch (err) {
      console.error("[WebSocket] Initial connect failed, retrying in 5s:", err);
      socket = null;
      connecting = false;
      setTimeout(() => WebSocketService.init({ password, userId, salt }), 5000);
    }

    connecting = false;
  },

  close: () => {
    if (socket) socket.close();
    socket = null;
    connecting = false;
  },
};

export default WebSocketService;